import { useRef, useState } from "react";
import axios from "axios";
import { Video as VideoIcon, Loader2, Plus, X, FileVideo } from "lucide-react";
import { toast } from "sonner";
import { useAuth, useLanguage, API } from "@/App";

/**
 * Multi-video uploader. Returns storage paths via onChange(paths: string[]).
 *
 *  Approval pipeline: when both `listingType` and `listingId` are provided, every
 *  successful upload is ALSO registered in the `media` collection with status
 *  `pending_review`. Videos remain hidden from customers until an admin approves them
 *  in the Admin → Media Review tab.
 *
 *  For brand-new listings where the id isn't known until after submit, the parent
 *  form should leave `listingId` undefined and call `POST /media` for each path
 *  itself after the listing has been created.
 *
 *  No cap on the number of videos. Accepted: MP4, MOV, WEBM. */
export const VideoUploader = ({ value = [], onChange, listingType, listingId }) => {
  const { token } = useAuth();
  const { t } = useLanguage();
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const willReview = !!(listingType && listingId);

  const handleSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    const uploaded = [];
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        const res = await axios.post(`${API}/uploads/video`, fd, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const path = res.data.path;
        uploaded.push(path);
        if (willReview) {
          try {
            await axios.post(`${API}/media`, {
              listing_type: listingType,
              listing_id:   listingId,
              url:          path,
              media_type:   "video",
            }, { headers: { Authorization: `Bearer ${token}` } });
          } catch (e) {
            console.warn("video review submission failed", e);
          }
        }
      } catch (err) {
        const detail = err?.response?.data?.detail;
        toast.error(typeof detail === "string" ? detail : t("فشل رفع الفيديو", "Video upload failed"));
      }
    }
    setUploading(false);
    if (willReview && uploaded.length) {
      toast.success(t("تم الرفع. الفيديوهات قيد المراجعة قبل الظهور للعملاء.", "Uploaded. Videos are under review before going live."));
    }
    onChange([...value, ...uploaded]);
    if (inputRef.current) inputRef.current.value = "";
  };

  const removeAt = (idx) => onChange(value.filter((_, i) => i !== idx));

  // Resolve a storage path into a playable URL — same convention as the rest of the app.
  const srcOf = (p) => (p.startsWith("http") ? p : `${API}/uploads/${p.replace(/^\/+/, "")}`);

  return (
    <div className="space-y-2" data-testid="video-uploader">
      <div className="flex items-center gap-2">
        <VideoIcon className="text-yellow-400" size={18} />
        <h4 className="text-white font-bold text-sm">{t("الفيديوهات", "Videos")}</h4>
      </div>

      {/* Preview grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {value.map((p, i) => (
          <div key={`${p}-${i}`} className="relative rounded-xl overflow-hidden bg-slate-900 aspect-video group" data-testid={`video-thumb-${i}`}>
            <video src={srcOf(p)} className="w-full h-full object-cover" controls preload="metadata" />
            <button
              type="button"
              onClick={() => removeAt(i)}
              className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-red-500/90 hover:bg-red-600 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
              aria-label={t("حذف", "Remove")}
              data-testid={`video-remove-${i}`}
            >
              <X size={14} />
            </button>
          </div>
        ))}

        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          data-testid="add-video-btn"
          className="aspect-video rounded-xl border-2 border-dashed border-slate-700 hover:border-yellow-500 hover:bg-yellow-500/5 flex flex-col items-center justify-center text-slate-400 hover:text-yellow-500 transition-colors gap-1"
        >
          {uploading ? <Loader2 className="animate-spin" size={22} /> : (
            <>
              <FileVideo size={22} />
              <span className="text-xs font-bold flex items-center gap-1"><Plus size={12} /> {t("إضافة فيديو", "Add video")}</span>
            </>
          )}
        </button>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="video/mp4,video/webm,video/quicktime,.mp4,.mov,.webm"
        multiple
        onChange={handleSelect}
        className="hidden"
        data-testid="video-upload-input"
      />
      <p className="text-xs text-slate-500">
        {t(
          "ارفع كل ما تشاء من الفيديوهات (MP4، MOV، WEBM) — تظهر للجمهور بعد مراجعة الإدارة.",
          "Upload as many videos as you like (MP4, MOV, WEBM) — they go live after admin review."
        )}
      </p>
    </div>
  );
};
